import { gql } from "@apollo/client";
import { taxCalculation, isDateRangeIn, values } from "./helper";
import {
  COURSE_TRIAL_DURATION_UNIT,
  CONTRACTABLE_BUFFER,
} from "@/lib/constants";
import dayjs from "@/lib/day";
import config from "@/lib/config";
import { GraphQLError } from "graphql";

export const typeDef = gql`
  type CoursePrice {
    id: ID
    price: Int
    payment_amount: Int
    validity_start_at: String
    validity_end_at: String
    validityEndAt: String
    campaignId: Int
    campaign: Campaign
  }

  type Course {
    id: ID!
    name: String!
    thumbnailUrl: String
    description: String
    validity_start_at: String
    validity_end_at: String
    price: Int
    priceWithoutTax: Int
    defaultPrice: Int
    defaultPriceWithoutTax: Int
    discount_price: Int
    active_pricing: CoursePrice
    rightIds: [Int]
    trial_flag: Boolean
    trial_date: String
    trialTerm: String
    trial_term_unit: String
    trialTermText: String
    trialText: String
    trialDate: String
    ref_id: String
    valuePlan: Boolean
    questionId: Int
    channel: ChannelMeta
    trialCampaignFlag: Boolean
    contractAllowed: Boolean
  }

  type Query {
    courseById(courseId: Int): Course
    courseByRightId(rightId: Int): Course
    courseByMetaId(metaId: Int): Course
  }
`;

export const resolvers = {
  Query: {
    courseById: async (_parent, { courseId }, { dataSources }) => {
      const res = await dataSources.courses.courseById(courseId);
      if (res) {
        return { ...res };
      } else {
        return null;
      }
    },
    courseByRightId: async (_parent, { rightId }, { dataSources }) => {
      const res = await dataSources.right.courses(rightId);
      if (res) {
        return res.courses[0];
      } else {
        return null;
      }
    },
    courseByMetaId: async (_parent, { metaId }, { dataSources }) => {
      const channel = await dataSources.meta.channelDetail(metaId);
      const right = channel?.values?.rights?.[0];
      if (right) {
        const res = await dataSources.right.courses(right.id);
        return res.courses[0];
      } else {
        return null;
      }
    },
  },
  Course: {
    id: (course) => course.id || course.course_id,
    thumbnailUrl: (course, _args, { req }) => {
      const noimage = "/images/noimage.png";
      return course?.thumbnail_url || noimage;
    },
    // キャンペーンが適用されていたらキャンペーンの価格になる
    price: (course) => course?.active_pricing?.payment_amount,
    priceWithoutTax: (course) => course?.active_pricing?.price,
    // course_pricingsの中からキャンペーンが設定されていない価格を取得する
    defaultPrice: (course, _args, { req }, info) => {
      try {
        const price = course?.course_pricings
          .filter((cp) => !cp?.campaign && !cp?.campaign_id)
          .map((cp) => cp.payment_amount)
          .reduce((acc, current) => (acc > current ? current : acc));
        return price;
      } catch (e) {
        throw new GraphQLError(e.message, {
          extensions: {
            code: "INTERNAL_SERVER_ERROR",
            operation: info?.operation?.operation,
            operationName: info?.operation?.name?.value,
            url: req?.url,
          },
        });
      }
    },
    defaultPriceWithoutTax: (course, _args, { req }, info) => {
      try {
        const price = course?.course_pricings
          .filter((cp) => !cp?.campaign && !cp?.campaign_id)
          .map((cp) => cp.price)
          .reduce((acc, current) => (acc > current ? current : acc));
        return price;
      } catch (e) {
        throw new GraphQLError(e.message, {
          extensions: {
            code: "INTERNAL_SERVER_ERROR",
            operation: info?.operation?.operation,
            operationName: info?.operation?.name?.value,
            url: req?.url,
          },
        });
      }
    },
    discount_price: async (course, _args, { dataSources }) => {
      const refId = course?.ref_id;
      const right = await dataSources.right.rightsByRefId(refId);
      const discount_price = taxCalculation(right?.values?.discount_price);
      return discount_price;
    },
    rightIds: (course) => course?.right_ids,
    valuePlan: (course) => {
      const id = course.id || course.course_id;
      return Number(id) === config.valuePlan.courseId;
    },
    trialCampaignFlag: async (course, _args, context) => {
      const [trialCampaignFlag] = await fakeTrialCampaignFlag(course, context);
      return trialCampaignFlag;
    },
    trialTerm: (course) => {
      if (course?.trial_term && course?.trial_term_unit) {
        const unit = COURSE_TRIAL_DURATION_UNIT[course.trial_term_unit].label;
        return `${course.trial_term}${unit}`;
      }
    },
    trialTermText: async (course, _args, context) => {
      if (course?.trial_term && course?.trial_term_unit) {
        const unit = COURSE_TRIAL_DURATION_UNIT[course.trial_term_unit].label;
        const [
          trialCampaignFlag,
          actual,
          fakeTrial,
        ] = await fakeTrialCampaignFlag(course, context);
        if (trialCampaignFlag && !actual) {
          return `${fakeTrial.term}${fakeTrial.unit}無料キャンペーン`;
        }
        return trialCampaignFlag
          ? `${course.trial_term}${unit}無料キャンペーン`
          : `${course.trial_term}${unit}無料トライアル`;
      }
      return null;
    },
    trialText: async (course, _args, { req, dataSources }) => {
      if (course?.trial_term && course?.trial_term_unit) {
        const unit = COURSE_TRIAL_DURATION_UNIT[course.trial_term_unit].label;
        const [
          trialCampaignFlag,
          actual,
          fakeTrial,
        ] = await fakeTrialCampaignFlag(course, {
          req,
          dataSources,
        });
        if (trialCampaignFlag && !actual) {
          return `${fakeTrial.term}${fakeTrial.unit}無料キャンペーン`;
        }
        return trialCampaignFlag
          ? `${course.trial_term}${unit}無料キャンペーン`
          : "無料トライアル";
      }
      return null;
    },
    trialDate: (course) => {
      if (course.trial_date) {
        return dayjs(course.trial_date).format("YYYY/MM/DD");
      } else {
        return "";
      }
    },
    questionId: (course) => {
      const questionId = course?.values?.question_id;
      if (typeof questionId === "object") {
        return questionId?.question_id;
      } else if (typeof questionId === "number") {
        return questionId;
      }
    },
    channel: async (parent, _args, { dataSources }) => {
      if (parent?.ref_id) {
        const res = await dataSources.right.rightsByRefId(parent.ref_id);
        const channel = await dataSources.meta.channelDetail(
          res?.values?.channel
        );
        return channel;
      }
    },
    contractAllowed: (course) => {
      return isDateRangeIn(
        course?.contractable_start_at,
        course?.contractable_end_at,
        { buffer: CONTRACTABLE_BUFFER, default: true }
      );
    },
  },
  CoursePrice: {
    id: (cp) => cp.course_pricing_id,
    campaignId: (cp) => {
      if (cp?.campaign_id) return Number(cp.campaign_id);
      if (cp?.campaign) return Number(cp?.campaign?.campaign_id);
      return null;
    },
  },
};

/**
  * @typedef FakeTrial
  * @property {number} term
  * @property {string} unit

/**
 * fakeTrialCampaignFlag.
 *
 * xx日間無料キャンペーン終了対応
 * 文言の出しわけにCourseのtrial_campaign_flagを使っているが、キャンペーン終了に伴いフラグがオフになる
 * フラグがオンのときにトライアルになったユーザーにはご利用状況ページでキャンペーン文言を出す必要がある
 * 特定のコースの場合にユーザーのsubscriptionの状態をみてフラグを偽装するようにする
 *
 * @param {Course} course
 * @param {Object} context
 * @param {Object} context.req
 * @param {Object} context.dataSources
 * @returns {Promise<(boolean|FakeTrial)[]>}
 */
async function fakeTrialCampaignFlag(course, { req, dataSources }) {
  const courseId = course.id || course.course_id;
  const target = config.trialCamapignTextDisplay.find(
    (v) => v.courseId === courseId
  );
  if (target && req.userToken) {
    // サブスクリプションを取得
    const subscription = await dataSources.subscribe.subscriptionFromCourseId(
      courseId
    );

    if (subscription && subscription?.status === "trial") {
      // if (subscription && subscription?.status === "trial") {
      // トライアル開始日時がendAt以前であればキャンペーンとみなす
      const trialStartAt = dayjs(new Date(subscription.trial_start_at));
      return [
        trialStartAt.isSameOrBefore(target.endAt, "secound"),
        false,
        { term: target.term, unit: target.unit },
      ];
    }
  }
  return [!!values(course, "trial_campaign_flag"), true];
}
